import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  afterRenderEffect,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { IsActiveMatchOptions, RouterLink, RouterLinkActive } from '@angular/router';

import { CATEGORY_REPOSITORY } from '@core/data/category/category.repository';
import { AsyncStatus } from '@core/models/async-status.type';
import { CategoryNodeState } from '@core/models/category.state';
import { TenantScopeService } from '@core/services/tenant-scope.service';

import { IconComponent } from '@shared/components/icon/icon.component';
import { SkeletonComponent } from '@shared/components/skeleton/skeleton.component';

/**
 * Horizontal category strip inside the header (HLD CAT-03). Pure navigation -
 * plain routerLink anchors, no form controls. Swaps to storefront-scoped
 * links when a tenant scope is active. The row scrolls horizontally at every
 * width, because how much of the curated set fits depends on the space left
 * over by the logo and utility links, not on a breakpoint: browser zoom
 * narrows the row while the desktop layout stays in force. The scrollbar is
 * hidden, so the edge fade is the only affordance and it is driven by the
 * measured scroll position rather than a media query.
 * While categories load, skeleton pills hold the row's width so the page
 * never shoves; on error the static links render alone - a quiet gap, not a
 * banner, because the shell chrome must stay calm.
 */
@Component({
  selector: 'app-category-subnav',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, IconComponent, SkeletonComponent],
  templateUrl: './category-subnav.component.html',
  styleUrl: './category-subnav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategorySubnavComponent {
  private readonly categoryRepository = inject(CATEGORY_REPOSITORY);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly tenantScope = inject(TenantScopeService);

  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');

  /** One placeholder width per curated subnav category (six roots). */
  protected readonly categoryPlaceholders = ['74px', '66px', '112px', '90px', '52px', '88px'];

  /** A subnav link is current when its params are a subset of the URL's. */
  protected readonly linkActiveOptions: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'subset',
    matrixParams: 'ignored',
    fragment: 'ignored'
  };

  private readonly _categories = signal<CategoryNodeState[]>([]);
  private readonly _status = signal<AsyncStatus>('loading');
  private readonly _hiddenBefore = signal(false);
  private readonly _hiddenAfter = signal(false);

  protected readonly categories = this._categories.asReadonly();
  protected readonly status = this._status.asReadonly();

  /** True when links sit outside the row on that side, so the edge fades. */
  protected readonly hiddenBefore = this._hiddenBefore.asReadonly();
  protected readonly hiddenAfter = this._hiddenAfter.asReadonly();

  constructor() {
    this.categoryRepository.tree().subscribe({
      next: tree => {
        this._categories.set(tree.filter(node => node.showInSubnav));
        this._status.set('success');
      },
      error: () => this._status.set('error')
    });

    // Re-measure after the link set changes; the observer below covers every
    // other cause of the row resizing, browser zoom included.
    afterRenderEffect({
      read: () => {
        this.categories();
        this.tenantScope.tenant();
        this.measure();
      }
    });

    afterNextRender(() => {
      const element = this.scroller()?.nativeElement;
      if (!element) return;

      const observer = new ResizeObserver(() => this.measure());
      observer.observe(element);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  /** Compares the scrolled position against the row's overflow, in CSS px. */
  protected measure(): void {
    const element = this.scroller()?.nativeElement;
    if (!element) return;

    // Sub-pixel layout means scrollLeft rarely lands exactly on the bounds.
    const overflow = element.scrollWidth - element.clientWidth;
    this._hiddenBefore.set(element.scrollLeft > 1);
    this._hiddenAfter.set(element.scrollLeft < overflow - 1);
  }
}
